import {
  balance,
  type ClientMessage,
  type ServerMessage,
} from "./types.ts";
import { clientDebug } from "../../shared/debug.ts";
import { makeResumeHandshake } from "./resume-storage.ts";

type MessageHandler = (msg: ServerMessage) => void;

// NetworkClient owns the WebSocket lifecycle: connect, reconnect with
// exponential backoff, buffered send before open, and typed inbound
// dispatch. Everything else in the client talks to this.
export class NetworkClient {
  private ws: WebSocket | null = null;
  private outbox: string[] = [];
  private handler: MessageHandler | null = null;
  // Explicit `number`: balance values are literal-narrowed by `as const`
  // and this field gets reassigned with the backoff product.
  private reconnectDelayMs: number = balance.network.reconnectInitialMs;
  private readonly maxReconnectDelayMs = balance.network.reconnectMaxMs;
  private closedByUser = false;
  // Phase 5.1 Step 1 — handshake gate. `handshakeComplete` is the
  // live-socket gate: false from open() until the server replies with
  // snapshot OR replay, at which point the buffered outbox flushes in
  // FIFO order. Resets on every close so reconnects re-buffer.
  // `hasCompletedHandshakeThisSession` is the page-lifecycle latch
  // that distinguishes a fresh load (force snapshot) from an in-session
  // reconnect (use stored resume token). It only flips false→true once
  // and stays true until the page itself is reloaded — at which point
  // a new NetworkClient instance is constructed with the field back
  // to false. Page-load detection without a `beforeunload` hook.
  private handshakeComplete = false;
  private hasCompletedHandshakeThisSession = false;

  constructor(
    private readonly wsUrl: string,
    private readonly playerId: string,
    private zoneId: string,
  ) {}

  // Phase 6: update the zone ID before reconnecting after a jump.
  updateZoneId(zoneId: string): void {
    this.zoneId = zoneId;
  }

  onMessage(handler: MessageHandler): void {
    this.handler = handler;
  }

  connect(): void {
    const url =
      `${this.wsUrl}?zoneId=${encodeURIComponent(this.zoneId)}` +
      `&playerId=${encodeURIComponent(this.playerId)}`;

    setConnectionStatus("connecting");
    const ws = new WebSocket(url);
    this.ws = ws;
    this.handshakeComplete = false;

    ws.addEventListener("open", () => {
      this.reconnectDelayMs = balance.network.reconnectInitialMs;
      setConnectionStatus("connected");

      // Phase 5.1 Step 1 — handshake goes out FIRST, bypassing the
      // outbox gate. Anything the game layer queued via send() while
      // offline (or before the socket opened) stays buffered until the
      // server replies snapshot/replay; the message handler below
      // flushes the queue FIFO at that point. Without this ordering,
      // a `move` queued by the LocalController ticker can race ahead
      // of `resume` on the wire and the server has nothing to attach
      // it to.
      const resumeMsg = makeResumeHandshake(
        window.localStorage,
        this.hasCompletedHandshakeThisSession,
      );
      ws.send(JSON.stringify(resumeMsg));
    });

    ws.addEventListener("message", (ev: MessageEvent) => {
      if (!this.handler) return;
      const raw = typeof ev.data === "string" ? ev.data : "";
      if (!raw) return;
      let msg: ServerMessage;
      try {
        // Inbound parse is one of the two permitted `any`-ish boundaries —
        // we cast to ServerMessage after parse and trust the server.
        msg = JSON.parse(raw) as ServerMessage;
      } catch (err) {
        // contracts:boundary — wire-inbound parse failure. The server
        // should never send invalid JSON, so if this fires in prod it's
        // a server bug or proxy tampering. Drop the message; the socket
        // stays open so the next (hopefully valid) message still arrives.
        // Dev-only trace: keeps prod quiet while still surfacing malformed
        // wire data to a developer running `vite dev` + devtools open.
        clientDebug("failed to parse server message", err);
        return;
      }
      // Phase 5.1 Step 1 — first snapshot/replay after open() flips
      // the handshake gate and flushes any queued sends. Game-layer
      // handler runs AFTER the flush so a reducer-driven send (none
      // today, but defense-in-depth) goes out on the same flushed
      // path. The flag stays true for the rest of this socket; on
      // close it resets so the next reconnect re-buffers.
      if (
        !this.handshakeComplete &&
        (msg.type === "snapshot" || msg.type === "replay")
      ) {
        this.handshakeComplete = true;
        this.hasCompletedHandshakeThisSession = true;
        for (const queued of this.outbox) ws.send(queued);
        this.outbox.length = 0;
      }
      // Handler invocation lives OUTSIDE the parse catch on purpose: a
      // bug inside handler would otherwise be misreported as a parse
      // failure. Let it propagate to the browser's unhandled-error path.
      this.handler(msg);
    });

    ws.addEventListener("close", () => {
      this.ws = null;
      this.handshakeComplete = false;
      setConnectionStatus("disconnected");
      if (this.closedByUser) return;
      this.scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // Let the close handler do the reconnect; errors always precede a close.
    });
  }

  send(msg: ClientMessage): void {
    const json = JSON.stringify(msg);
    // Phase 5.1 Step 1 — gate on `handshakeComplete`, not just socket
    // OPEN state. Sends issued between open() and the handshake reply
    // queue alongside sends issued while disconnected; both flush in
    // FIFO order when the gate flips. Resume envelopes themselves
    // bypass this gate via the open handler's direct ws.send().
    if (
      this.ws &&
      this.ws.readyState === WebSocket.OPEN &&
      this.handshakeComplete
    ) {
      this.ws.send(json);
    } else {
      this.outbox.push(json);
    }
  }

  close(): void {
    this.closedByUser = true;
    this.ws?.close();
    this.ws = null;
  }

  // Phase 5 Step 17 — close the current socket and let the existing
  // close-handler reschedule a reconnect. Used by the expedition-entry
  // accept flow so the Worker routes the new connection through
  // PlayerDO.getResumeTarget and lands us on the ExpeditionDO. The
  // outbox preserves any messages that were sent pre-open on the new
  // socket.
  reconnect(): void {
    if (!this.ws) return;
    this.ws.close();
    this.ws = null;
    // Close handler will run scheduleReconnect() because closedByUser
    // is still false. Nothing else to do here.
  }

  private scheduleReconnect(): void {
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(
      this.maxReconnectDelayMs,
      this.reconnectDelayMs * balance.network.reconnectBackoffFactor,
    );
    // Random jitter up to jitterRatio * delay prevents thundering-herd
    // reconnects after a shared edge failure — without it, every client
    // in a zone retries in lockstep and slams the same ZoneDO. The
    // stored reconnectDelayMs stays un-jittered so backoff progression
    // remains predictable; only the actual wait is randomized.
    const jitter = Math.random() * balance.network.reconnectJitterRatio * delay;
    setTimeout(() => {
      if (this.closedByUser) return;
      this.connect();
    }, delay + jitter);
  }
}

// DOM signal read by the browser harness (scripts/test-browser.ts) to
// wait for the socket to be live before asserting state. Contract: the
// attribute value is always one of "connecting" | "connected" |
// "disconnected"; the initial value is set in client/index.html.
type ConnectionStatus = "connecting" | "connected" | "disconnected";
function setConnectionStatus(status: ConnectionStatus): void {
  if (typeof document === "undefined") return;
  document.body.dataset.connectionStatus = status;
}

export function getOrCreatePlayerId(): string {
  const key = "nexus-forge:playerId";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(key, id);
  return id;
}
